import React, { Suspense, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
    Route,
    BrowserRouter as Router,
    Switch,
    Redirect,
    useHistory,
    useLocation
} from 'react-router-dom';
import { Loading } from '~/ui/components/Loading';
import constants from '~/utils/constants';
import { ClientProvider } from '~/components/ClientProvider';
import { EquipmentSocketProvider } from '~/components/EquipmentSocketProvider';
import { WebSocketProvider } from '~/components/WebSocketProvider';
import { MonitoringProvider } from '~/components/MonitoringProvider';
import { BaseProcessIndicatorProvider } from '~/contexts/ProcessIndicatorContext';
import { AuroraErrorBoundary, AuroraProvider } from '@wisesystems/aurora-ui';
import '@wisesystems/aurora-ui/styles';
import { sendDatadogError } from '~/datadogService';
import { getAuroraRequestContext } from '~/components/App/getAuroraRequestContext';

/**
 * Global error handler for the Aurora ErrorBoundary.
 * Logs errors to Datadog only. Auth redirects are handled by the auth guard
 * in the catch-all route — the error boundary should not participate in auth flow.
 */
const handleAuroraError = (error) => {
    sendDatadogError(error, { source: 'AuroraErrorBoundary' });
};

/**
 * Checks whether the user has an active session or is mid-SSO callback.
 * Returns true if mcwAppInit exists OR if the URL has an OAuth code/origin
 * (SSO callback in progress — useLogin will exchange the code for tokens).
 */
const hasActiveSessionOrSSOCallback = () => {
    const hasSession = Boolean(
        localStorage.getItem(constants.localStorageKeys.MCW_APP_INIT)
    );
    if (hasSession) return true;

    const params = new URLSearchParams(window.location.search);
    const hasSSOCode = Boolean(params.get('code'));
    const hasCrossDomainOrigin =
        Boolean(params.get('origin')) && Boolean(params.get('path'));
    return hasSSOCode || hasCrossDomainOrigin;
};

const DriverBooking = React.lazy(() => import('~/components/DriverBooking'));
const DriverManagement = React.lazy(() =>
    import('~/components/DriverManagementPage')
);
const DriverVehicleManagement = React.lazy(() =>
    import('~/components/DriverVehicleManagementPage')
);
const HomePage = React.lazy(() => import('~/components/HomePage'));
const LoginPage = React.lazy(() => import('~/components/LoginPage'));
const LogoutPage = React.lazy(() =>
    import('~/components/LoginPage/LogoutPage')
);
const RootPage = React.lazy(() => import('~/components/LoginPage/RootPage'));
const MainLayout = React.lazy(() => import('~/components/MainLayout'));
const MapPage = React.lazy(() => import('~/components/MapPage'));
const NotificationManagementPage = React.lazy(() =>
    import('~/components/NotificationManagementPage')
);
const CustomerManagementPage = React.lazy(() =>
    import('~/components/CustomerManagementPage')
);
const CustomerSearchPage = React.lazy(() =>
    import('~/components/CustomerSearchPage')
);
const OptimizationConfigurationPage = React.lazy(() =>
    import('~/components/OptimizationConfigurationPage')
);
const ReportsManagement = React.lazy(() => import('~/components/ReportsPage'));
const SettingsPage = React.lazy(() => import('~/components/SettingsPage'));
const TaskManagementPage = React.lazy(() =>
    import('~/components/TaskManagementPage')
);
const ZoneManagementPage = React.lazy(() =>
    import('~/components/ZoneManagementPage')
);
const Unauthorized = React.lazy(() =>
    import('~/components/ErrorPage/Unauthorized')
);
const UsersAndPermissionsPage = React.lazy(() =>
    import('~/components/UsersAndPermissionsPage')
);
const CostConfigurationPage = React.lazy(() =>
    import('~/components/CostConfigurationPage')
);
const AdvancedConfigurationPage = React.lazy(() =>
    import('~/components/AdvancedConfigurationPage')
);
const DoorDashSettingsPage = React.lazy(() =>
    import('~/components/DoorDashSettingsPage')
);
const WorkflowManagement = React.lazy(() =>
    import('~/components/WorkflowPage')
);

const AuroraDashboardWithNavigation = React.lazy(() =>
    import('~/components/AuroraDashboardWithNavigation')
);

/**
 * Thin wrapper rendered inside <Router> so it can access React Router hooks.
 * Passes onNavigate and currentPath to AuroraProvider (NOVA-1802, NOVA-1803)
 * so ChatHeader "See chat in dashboard" uses history.push instead of
 * window.location.href, and isDashboardView detection stays in sync with
 * the router.
 */
const AuroraProviderWrapper = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const store = useStore();
    const auroraScope = useSelector((state) => {
        const selectedClientIds = Object.keys(state.selectedClients || {});
        return JSON.stringify([
            state.currentUser?.id,
            selectedClientIds.sort(),
            Object.keys(state.clients || {}).sort(),
            (state.currentUser?.access || [])
                .filter(({ client }) => selectedClientIds.includes(client))
                .map(({ client }) => client)
        ]);
    });
    const getRequestContext = useCallback(
        () => getAuroraRequestContext(store.getState()),
        // Aurora resets its own chat state on a scope change, not on token refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [store, auroraScope]
    );

    const handleNavigate = useCallback(
        (path) => {
            history.push(path);
        },
        [history]
    );

    return (
        <AuroraProvider
            getRequestContext={getRequestContext}
            currentPath={location.pathname}
            onNavigate={handleNavigate}
        >
            {children}
        </AuroraProvider>
    );
};

/**
 * Authenticated route tree — extracted so it can be rendered both inside
 * AuroraProvider (normal) and standalone (Aurora error fallback).
 */
const AuthenticatedRoutes = () => {
    const { url } = constants;
    return (
        <Switch>
            <Route path={url.HOME}>
                <HomePage />
            </Route>
            <Route path={url.DRIVER_BOOKING}>
                <DriverBooking />
            </Route>
            <Route path={url.DRIVER_MANAGEMENT}>
                <DriverManagement />
            </Route>
            <Route path={url.DRIVER_MANAGEMENT_DATE_AGNOSTIC}>
                <DriverVehicleManagement />
            </Route>
            <Route path={url.WORKFLOW}>
                <Redirect to={url.MAP} />
            </Route>
            <Route path={url.MAP}>
                <MapPage />
            </Route>
            <Route path={url.COST_CONFIGURATION}>
                <CostConfigurationPage />
            </Route>
            <Route exact path={url.USERS_AND_PERMISSIONS}>
                <UsersAndPermissionsPage />
            </Route>
            <Route path={url.TASK_MANAGEMENT}>
                <TaskManagementPage />
            </Route>
            <Route exact path={url.NOTIFICATION_MANAGEMENT}>
                <NotificationManagementPage />
            </Route>
            <Route path={url.REPORTS}>
                <ReportsManagement />
            </Route>
            <Route path={url.SETTINGS}>
                <SettingsPage />
            </Route>
            <Route exact path={url.CUSTOMER_DETAILS}>
                <CustomerManagementPage />
            </Route>
            <Route path={url.CUSTOMER_MANAGEMENT}>
                <CustomerSearchPage />
            </Route>
            <Route exact path={url.OPTIMIZATION_CONFIGURATION}>
                <OptimizationConfigurationPage />
            </Route>
            <Route path={url.ADVANCED_CONFIGURATION}>
                <AdvancedConfigurationPage />
            </Route>
            <Route path={url.DOORDASH_SETTINGS}>
                <DoorDashSettingsPage />
            </Route>
            <Route path={url.ZONE_MANAGEMENT}>
                <ZoneManagementPage />
            </Route>
            <Route exact path={url.WORKFLOWS}>
                <WorkflowManagement />
            </Route>
            <Route exact path={url.AURORA_AI}>
                <AuroraDashboardWithNavigation />
            </Route>
        </Switch>
    );
};

const App = () => {
    const { url } = constants;

    return (
        <Router basename={url.ROOT}>
            <Suspense fallback={<Loading fillViewport />}>
                <Switch>
                    <Route exact path="/">
                        <RootPage />
                    </Route>
                    <Route path={url.LOGIN}>
                        <LoginPage />
                    </Route>
                    <Route path={url.LOGOUT}>
                        <LogoutPage />
                    </Route>
                    <Route path={url.UNAUTHORIZED}>
                        <Unauthorized />
                    </Route>
                    <Route
                        render={() => {
                            if (!hasActiveSessionOrSSOCallback()) {
                                return <Redirect to={url.LOGIN} />;
                            }
                            return (
                                <>
                                    <MonitoringProvider />
                                    <ClientProvider />
                                    <WebSocketProvider />
                                    <EquipmentSocketProvider />
                                    <BaseProcessIndicatorProvider>
                                        <MainLayout>
                                            <AuroraErrorBoundary
                                                onError={handleAuroraError}
                                                fallback={
                                                    <AuthenticatedRoutes />
                                                }
                                            >
                                                <AuroraProviderWrapper>
                                                    <AuthenticatedRoutes />
                                                </AuroraProviderWrapper>
                                            </AuroraErrorBoundary>
                                        </MainLayout>
                                    </BaseProcessIndicatorProvider>
                                </>
                            );
                        }}
                    />
                    <Route path={[url.UNAUTHORIZED, '*']}>
                        <Unauthorized />
                    </Route>
                </Switch>
            </Suspense>
        </Router>
    );
};

export default App;
